import { useTicketUpdate } from 'features/libra/ticket/TicketUpdateContext'
import { useGeckoNotifications } from 'features/notifications'
import get from 'lodash/get'
import { useCallback, useMemo } from 'react'

import {
  NiiReportField,
  NiiReportTicketProps,
  niiReportTicketPropsSchema,
} from './niiReportApi'
import { useNiiReportData } from './NiiReportDataContext'

// Helper type to unwrap array types for reference values
type UnwrapArray<T> = T extends ReadonlyArray<infer U> ? U : T

// For array fields, we return the element type; for non-array fields, the field type itself
type ReferenceValueType<K extends NiiReportField> = UnwrapArray<
  NiiReportTicketProps[K]
>

/**
 * Unwrap Zod schema wrappers (ZodDefault, ZodOptional, ZodReadonly) to get to the inner schema.
 */
function unwrapSchema(schema: unknown): unknown {
  if (typeof schema !== 'object' || schema === null) {
    return schema
  }

  let current: unknown = schema

  // Keep unwrapping while we find wrapped schemas
  while (current && typeof current === 'object') {
    // Check for _def.innerType (used by ZodDefault, ZodOptional) or innerType directly
    if ('_def' in current) {
      const def = (current as { _def: unknown })._def
      if (def && typeof def === 'object' && 'innerType' in def) {
        current = (def as { innerType: unknown }).innerType
        continue
      }
    }

    // Check for unwrap() method (used by ZodReadonly)
    if (
      'unwrap' in current &&
      typeof (current as { unwrap: unknown }).unwrap === 'function'
    ) {
      current = (current as { unwrap: () => unknown }).unwrap()
      continue
    }

    // No more wrappers found
    break
  }

  return current
}

/**
 * Parse a reference value, accepting both scalars and arrays for any field type.
 * Always returns values in a format that can be flattened.
 */
function parseReferenceValue<K extends NiiReportField>(
  refValue: unknown,
  fieldSchema: unknown
): Array<ReferenceValueType<K>> | undefined {
  // Unwrap any schema wrappers to get to the actual schema
  const unwrappedSchema = unwrapSchema(fieldSchema)

  if (
    typeof unwrappedSchema !== 'object' ||
    unwrappedSchema === null ||
    !('safeParse' in unwrappedSchema)
  ) {
    return undefined
  }

  const schema = unwrappedSchema as {
    safeParse: (val: unknown) => { success: boolean; data?: unknown }
    element?: unknown
  }

  // Determine the element schema (for array fields) or use the field schema itself
  const elementSchema =
    'element' in schema &&
    schema.element &&
    typeof schema.element === 'object' &&
    schema.element !== null &&
    'safeParse' in schema.element
      ? (schema.element as {
          safeParse: (val: unknown) => { success: boolean; data?: unknown }
        })
      : schema

  // Handle array of values
  if (Array.isArray(refValue)) {
    const parsed = refValue
      .map((item) => {
        const result = elementSchema.safeParse(item)
        return result.success ? (result.data as ReferenceValueType<K>) : null
      })
      .filter((item): item is ReferenceValueType<K> => item !== null)
    return parsed.length > 0 ? parsed : undefined
  }

  // Handle single value
  const result = elementSchema.safeParse(refValue)
  if (result.success) {
    return [result.data as ReferenceValueType<K>]
  }

  return undefined
}

/**
 * Extract and flatten reference values from ticket props.
 * This is the core logic for resolving reference field paths and flattening the results.
 */
export function extractReferenceValues<K extends NiiReportField>(
  field: K,
  referenceFieldPaths: ReadonlyArray<string>,
  ticketProps: Record<string, unknown>,
  onError?: (fieldPath: string, value: unknown) => void
): ReadonlyArray<ReferenceValueType<K>> {
  const fieldSchema = niiReportTicketPropsSchema.unwrap().shape[field]

  return referenceFieldPaths.flatMap((referenceField) => {
    const refValue = get(ticketProps, referenceField)
    if (!refValue) {
      return []
    }

    const parsed = parseReferenceValue<K>(refValue, fieldSchema)
    if (parsed === undefined) {
      onError?.(referenceField, refValue)
      return []
    }
    return parsed
  })
}

export function useReferenceValues<K extends NiiReportField>(
  field: K
): {
  values: ReadonlyArray<ReferenceValueType<K>>
} {
  const { fieldValues } = useTicketUpdate()
  const {
    niiConfig: { referenceFields: configReferenceFields },
  } = useNiiReportData()

  const referenceFields = useMemo<ReadonlyArray<string>>(
    () => configReferenceFields?.[field] ?? [],
    [configReferenceFields, field]
  )

  const { errorNotification } = useGeckoNotifications()

  const handleError = useCallback(
    (fieldPath: string, value: unknown) => {
      errorNotification(
        `Invalid reference value for ${field}: ${JSON.stringify(value)}`
      )
      console.error(
        `Invalid reference value for ${field} from ${fieldPath}:`,
        value
      )
    },
    [errorNotification, field]
  )

  const referenceValues = useMemo<ReadonlyArray<ReferenceValueType<K>>>(
    () =>
      extractReferenceValues<K>(
        field,
        referenceFields,
        fieldValues as Record<string, unknown>,
        handleError
      ),
    [field, referenceFields, fieldValues, handleError]
  )

  return {
    values: referenceValues,
  }
}
